import { useEffect, useState } from "react";

import {
  Select,
  Button,
  AutocompleteMultipleSelect,
  TableMetadata,
  TableStateWithFilters,
  useTable,
  ColumnSortEnum,
  Spinner,
  Checkbox,
  Typography,
  Tooltip,
} from "@king-ict/eupisi-ui/components";
import { PagedResult, CodeBook } from "@king-ict/eupisi-ui/models";
import { ExportTypeEnum } from "@king-ict/eupisi-ui/models/Export/ExportTypeEnum";
import {
  codeBookToSelectData,
  hrDate,
  nameof,
} from "@king-ict/eupisi-ui/utils";
import { Row, Col, Modal as BSModal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { getDepartmentFilteredList } from "../../api/Department/DepartmentApi";
import { searchInstitutionNew } from "../../api/Institution/InstitutionApi";
import {
  dataSynchronization,
  getSchoolCandidateDepartmentProgram,
  updateRequestsProgramAndDepartment,
} from "../../api/SchoolCandidate/SchoolCandidateApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  FormCard,
  ListTable,
  SearchCard,
  ToolbarTop,
  InstitutionSelect,
  ToolbarEdit,
} from "../../components";
import DepartmentModal from "../../features/Departments/DepartmentModal";
import { useDidUpdateEffect, useInput } from "../../hooks";
import { useAuth } from "../../hooks/auth/useAuth";
import {
  SchoolYear,
  ValidationFailure,
  InstitutionCodeBook,
  Role,
  Selectable,
} from "../../models";
import {
  Department,
  DepartmentScheduleSearch,
  DepartmentListSort,
  DepartmentEditRequest,
  DepartmentCreateRequest,
  SchoolCandidateDepartmentProgram,
  SchoolCandidateForSchedule,
  SaveSchoolCandidateForScheduleInstitutionAssignment,
  SyncDataRequest,
  SyncDataDto,
} from "../../models/Department";
import { ImportantDateEventEnum } from "../../models/ImportantDate/ImportantDateEventEnum";
import { InstitutionCodeBookSearch } from "../../models/Institution";
import {
  CommercialProgram,
  RequestSyncStatus,
  RequestType,
} from "../../models/Request";
import { SaveResult } from "../../models/Shared";
import { ClaimType } from "../../models/User";
import {
  useGetInstitutionListForUserQuery,
  useGetSchoolYearCurrentQuery,
  useGetSchoolYearListQuery,
} from "../../queries/codeBookQuery";
import { TableUtils, MsgUtils, ObjectUtils } from "../../utils";

/**
 * Only Global and County level users.
 * @returns
 */
export function SchoolCandidateDepartmentSchedule() {
  const { t } = useTranslation();
  const history = useHistory();
  const { handleChange } = useInput();
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const { data: currentSchoolYear } = useGetSchoolYearCurrentQuery();

  const createDefaultUpdate = (): SyncDataRequest => {
    return {
      data: [],
    };
  };

  // EDIT
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalTableData, setOriginalTableData] = useState<PagedResult<
    SchoolCandidateForSchedule & Selectable
  > | null>(null);
  const [dataToUpdate, setDataToUpdate] = useState<SyncDataRequest>(
    createDefaultUpdate()
  );

  const handleEditClick = () => {
    if (!isEditMode) {
      setOriginalTableData(tableData);
    }
    setIsEditMode((prevState) => !prevState);
  };
  const handleSaveClick = () => {
    //1. validacija
    if (
      dataToUpdate.data.length === 0 ||
      dataToUpdate.data.filter((x) => !x.departmentId || !x.programId).length >
        0
    ) {
      return;
    }

    setUpdateModalVisible(true);
    // setIsEditMode(false);
  };

  const handleCancelClick = () => {
    if (originalTableData) {
      setTableData(originalTableData);
    }
    setIsEditMode(false);
    setDataToUpdate(createDefaultUpdate());
  };

  const [programs, setPrograms] = useState<CodeBook[]>([]);
  const [departments, setDepartments] = useState<CodeBook[]>([]);

  //SYNC MODAL
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [syncResult, setSyncResult] = useState<SaveResult<SyncDataRequest>>();
  const [updateDataResult, setUpdateDataResult] =
    useState<SaveResult<SyncDataRequest>>();

  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [updateSuccessModalVisible, setUpdateSuccessModalVisible] =
    useState(false);
  const [updateErrorModalVisible, setUpdateErrorModalVisible] = useState(false);

  //EDIT DATA
  const { data: schoolYears } = useGetSchoolYearListQuery();
  const { data: institutions } = useGetInstitutionListForUserQuery();
  const [filteredInstitutions, setFilteredInstitutions] = useState<
    InstitutionCodeBook[]
  >([]);

  const openModal = () => {
    if (
      tableData.results.filter(
        (x) => x.isSelected && (!x.selectedProgram || !x.selectedDepartment)
      ).length > 0
    ) {
      return;
    }
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
  };

  const closeSuccessModal = () => {
    setIsSuccessModalVisible(false);
  };

  const closeErrorModal = () => {
    setIsErrorModalVisible(false);
    setSyncResult(undefined);
  };

  const closeUpdateModal = () => {
    setUpdateModalVisible(false);
  };

  const closeUpdateSuccessModal = () => {
    setIsEditMode(false);
    setDataToUpdate(createDefaultUpdate());
    setUpdateModalVisible(false);
    setUpdateSuccessModalVisible(false);
  };

  const closeUpdateErrorModal = () => {
    setUpdateModalVisible(false);
    setUpdateErrorModalVisible(false);
    setUpdateDataResult(undefined);
  };

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  //STORAGE KEY
  const storageKey = "SchoolCandidateForScheduleSearchTableState";
  //INIT TABLE STATE WITH FILTERS - could be temporary stored in local session storage
  const createInitialTableState = () => {
    return TableUtils.CreateInitialTableState<DepartmentScheduleSearch>(
      storageKey,
      TableUtils.tableDefaultState(
        DepartmentListSort.Label,
        ColumnSortEnum.ASC,
        50
      )
    );
  };

  const defaultInstitutionId: number =
    // location only represents an Institution if ClaimType = Institution
    auth.currentRole?.claimType === ClaimType.Institution &&
    (auth.currentRole?.locations?.length ?? 0) > 0 &&
    auth.currentRole?.locations &&
    auth.currentRole?.locations[0]
      ? auth!.currentRole!.locations![0]
      : ((institutions && institutions.length > 0
          ? institutions[0].id
          : 0) as number);

  const [initialTableState] = useState<
    TableStateWithFilters<DepartmentScheduleSearch>
  >(() => createInitialTableState());
  //INIT SEARCH FILTER

  const initialDepartmentScheduleSearch: DepartmentScheduleSearch = {
    schoolYearId:
      initialTableState.schoolYearId ??
      currentSchoolYear?.id ??
      (schoolYears && schoolYears.length > 0 ? schoolYears[0].id : 0),
    institutionId: initialTableState.institutionId ?? defaultInstitutionId,
    requestTypeId: initialTableState.requestTypeId,
    synchonizationStatusId: initialTableState.synchonizationStatusId,
  };
  const createInitialDepartmentScheduleSearch =
    (): DepartmentScheduleSearch => {
      // return initialTableState || initialDepartmentScheduleSearch;
      return initialDepartmentScheduleSearch;
    };

  //TABLE DATA
  const defaultTableData = TableUtils.tableDefaultData<
    SchoolCandidateForSchedule & Selectable
  >();
  const [tableData, setTableData] =
    useState<PagedResult<SchoolCandidateForSchedule & Selectable>>(
      defaultTableData
    );
  //SET SEARCH FILTERS
  const [departmentScheduleSearch, setDepartmentScheduleSearch] =
    useState<DepartmentScheduleSearch>(() =>
      createInitialDepartmentScheduleSearch()
    );
  const [tableSearch, setTableSearch] = useState<DepartmentScheduleSearch>(() =>
    createInitialDepartmentScheduleSearch()
  );
  const [filterChanged, setFilterChanged] = useState<boolean>(false);
  const [clearFilter, setClearFilter] = useState<boolean>(true);
  //TABLE METADATA
  const tableMetadata: TableMetadata<SchoolCandidateForSchedule & Selectable> =
    {
      rowKey: "requestId",
      columns: [
        {
          columnKey: "select",
          header: "",
          accessor: (item: SchoolCandidateForSchedule & Selectable) => {
            return item.synchronizationStatus ===
              RequestSyncStatus.Deployment ? (
              <div>
                <Checkbox
                  label={""}
                  name={nameof<typeof item>("requestId")}
                  value={item.requestId}
                  checked={item.isSelected}
                  onChange={handleSchoolCandidateCheckedChange}
                  disabled={!isSchoolYearEditable || isEditMode}
                />
              </div>
            ) : null;
          },
        },
        {
          columnKey: "name",
          header: t("common.departmentSchedule.name"),
          accessor: (item: SchoolCandidateForSchedule) => {
            return (
              <div>
                <div className={"text-nowrap"}>{item.name}</div>
                <div className={"text-nowrap"}>
                  {t("common.schoolCandidateModel.oib")}:&nbsp;{item.oib}
                </div>
              </div>
            );
          },
          sortBy: 1,
        },
        {
          columnKey: "dateOfBirth",
          header: t("common.departmentSchedule.dateOfBirth"),
          accessor: (item: SchoolCandidateForSchedule) => {
            return (
              <div className={"text-nowrap"}>{hrDate(item.dateOfBirth)}</div>
            );
          },
          sortBy: 2,
        },
        {
          columnKey: "requestType",
          header: t("common.departmentSchedule.requestType"),
          accessor: (item: SchoolCandidateForSchedule) => {
            return (
              <div>
                <div className={"text-nowrap"}>{item.requestType}</div>
              </div>
            );
          },
          sortBy: 3,
        },
        {
          columnKey: "commercialProgram",
          header: t("common.departmentSchedule.commercialProgram"),
          accessor: (item: SchoolCandidateForSchedule) => {
            return (
              <div>
                <div className={"text-nowrap"}>
                  {
                    commercialPrograms.find(
                      (v) => v.id === item.commercialProgram
                    )?.name
                  }
                </div>
              </div>
            );
          },
        },
        {
          columnKey: "electiveSubjects",
          header: t("common.departmentSchedule.electiveSubjects"),
          accessor: (item: SchoolCandidateForSchedule) => {
            return (
              <div>
                {item.electiveSubjects.map((subject) => (
                  <div key={subject} className={"text-nowrap"}>
                    {subject}
                  </div>
                ))}
              </div>
            );
          },
          // sortBy: 4,
        },
        {
          columnKey: "foreignLanguages",
          header: t("common.departmentSchedule.foreignLanguages"),
          accessor: (item: SchoolCandidateForSchedule) => {
            return (
              <div>
                {item.foreignLanguages.map((language) => (
                  <div key={language} className={"text-nowrap"}>
                    {language}
                  </div>
                ))}
              </div>
            );
          },
          // sortBy: 5,
        },
        {
          columnKey: "selectedProgram",
          header: t("common.departmentSchedule.program"),
          accessor: (item: SchoolCandidateForSchedule & Selectable) => {
            return (
              <div>
                {(item.isSelected &&
                  item.synchronizationStatus ===
                    RequestSyncStatus.Deployment) ||
                (isEditMode &&
                  (item.synchronizationStatus ===
                    RequestSyncStatus.Deployment ||
                    item.synchronizationStatus ===
                      RequestSyncStatus.ManualInput)) ? (
                  <Select
                    name={nameof<typeof item>("selectedProgram")}
                    value={item.selectedProgram?.id || undefined}
                    selectData={codeBookToSelectData(programs)}
                    onChange={(event) => {
                      const selectedProgramId = event.target.value;
                      handleProgramChange(selectedProgramId, item.requestId);
                    }}
                    errorText={
                      (item.isSelected || isEditMode) && !item.selectedProgram
                        ? t("validations.required")
                        : undefined
                    }
                  />
                ) : (
                  <div className={"text-nowrap"}>
                    {item.selectedProgram?.name}
                  </div>
                )}
              </div>
            );
          },
          // sortBy: 6,
        },
        {
          columnKey: "selectedDepartment",
          header: t("common.departmentSchedule.department"),
          accessor: (item: SchoolCandidateForSchedule & Selectable) => {
            return (
              <div>
                {(item.isSelected &&
                  item.synchronizationStatus ===
                    RequestSyncStatus.Deployment) ||
                (isEditMode &&
                  (item.synchronizationStatus ===
                    RequestSyncStatus.Deployment ||
                    item.synchronizationStatus ===
                      RequestSyncStatus.ManualInput)) ? (
                  <Select
                    name={nameof<typeof item>("selectedDepartment")}
                    value={item.selectedDepartment?.id || undefined}
                    selectData={codeBookToSelectData(departments)}
                    onChange={(event) => {
                      const selectedDepartmentId = event.target.value;
                      handleDepartmentChange(
                        selectedDepartmentId,
                        item.requestId
                      );
                    }}
                    errorText={
                      (item.isSelected || isEditMode) &&
                      !item.selectedDepartment
                        ? t("validations.required")
                        : undefined
                    }
                  />
                ) : (
                  <div className={"text-nowrap"}>
                    {item.selectedDepartment?.name}
                  </div>
                )}
              </div>
            );
          },
          // sortBy: 7,
        },
        {
          columnKey: "synchronizationStatus",
          header: t("common.departmentSchedule.statusSync"),
          accessor: (item: SchoolCandidateForSchedule) => {
            return (
              <div className={"text-nowrap"}>
                {
                  statuses.find((v) => v.id === item.synchronizationStatus)
                    ?.name
                }
              </div>
            );
          },
          // sortBy: 8,
        },
      ],
      rowToolTip: () => undefined,
    };

  //SET TABLE
  /** The table metadata. The search is performed when the tableState changes (useEffect)
   * which is triggerd by a call to setFilters. Why are we doing this?
   */

  const [tableProps, tableState, setFilters] =
    useTable<DepartmentScheduleSearch>({
      metadata: tableMetadata,
      data: tableData!.results,
      pagination: true,
      paginationPage: initialTableState.page,
      paginationPageSize: initialTableState.pageSize as any,
      totalItems: tableData!.rowCount,
      defaultSort: {
        order: initialTableState.order,
        sortBy: initialTableState.sortBy,
      },
    });

  //SET FILTERS
  const [statuses] = useState<CodeBook[]>(
    MsgUtils.getEnumToCodeBook(RequestSyncStatus, "requestSyncStatus")
  );
  const [commercialPrograms] = useState<CodeBook[]>(
    MsgUtils.getEnumToCodeBook(CommercialProgram, "commercialProgram")
  );
  const [types] = useState<CodeBook[]>(
    MsgUtils.getEnumToCodeBook(RequestType, "requestType").filter(
      (type) => type.id !== RequestType.TemporaryExemption
    )
  );

  const [updateSyncDataStatus, setUpdateSyncDataStatus] =
    useState<SyncDataRequest>(createDefaultUpdate());
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [sendStatusEnabled, setSendStatusEnabled] = useState<boolean>(false);
  const [isSchoolYearEditable, setIsSchoolYearEditable] =
    useState<boolean>(false);
  //const [updateInstitutionAssignment, setUpdateInstitutionAssignment] = useState<SaveSchoolCandidateForScheduleInstitutionAssignment>(createDefaultUpdate());
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [initialSearch, setInitialSearch] = useState<boolean>(false);

  //    useDidUpdateEffect(() => {
  //        //used when user refresh/reload page
  //        const newDepartmentScheduleSearch: DepartmentScheduleSearch = {
  //            ...departmentScheduleSearch,
  //        }
  //        if (currentSchoolYear && departmentScheduleSearch.schoolYearId !== currentSchoolYear?.id && schoolYears) {
  //            newDepartmentScheduleSearch.schoolYearId = currentSchoolYear?.id;
  //        }
  //        // if (institutions && institutions.length > 0) {
  //        //     const institution = institutions[0];
  //        //     switch (auth.currentRole?.claimType) {
  //        //         case ClaimType.County:
  //        //         case ClaimType.Institution:
  //        //             {
  //        //                 const countyId = institution.countyId;
  //        //                 initialSchoolCandidateSearch.uoguCountyId = countyId;
  //        //                 newSchoolCandidateSearch.uoguCountyId = countyId;
  //        //                 break;
  //        //             }
  //        //     }
  //        // };
  //        setDepartmentScheduleSearch(newDepartmentScheduleSearch);
  //        setTableSearch(newDepartmentScheduleSearch);
  //        setClearFilter(true);
  //        search();
  //    // eslint-disable-next-line react-hooks/exhaustive-deps
  //    }, [currentSchoolYear, schoolYears /*, institutions*/]);

  useEffect(() => {
    if (clearFilter) {
      setClearFilter(false);
      setFilterChanged(false);
    } else setFilterChanged(true);
    TableUtils.RemoveSessionStorageItem(storageKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentScheduleSearch]);

  useEffect(() => {
    if (
      schoolYears &&
      schoolYears.length > 0 &&
      !departmentScheduleSearch.schoolYearId
    ) {
      var schoolYear = schoolYears.find((v) => v.isCurrent);
      if (schoolYear) {
        const newSearch: DepartmentScheduleSearch = {
          ...departmentScheduleSearch,
          schoolYearId: schoolYear.id,
        };
        setDepartmentScheduleSearch(newSearch);
      }
    }
  }, [schoolYears]);

  useEffect(() => {
    if (
      institutions &&
      institutions.length > 0 &&
      !departmentScheduleSearch.institutionId
    ) {
      var institution = institutions[0];
      const newSearch: DepartmentScheduleSearch = {
        ...departmentScheduleSearch,
        institutionId: institution.id,
      };
      setDepartmentScheduleSearch(newSearch);
    }
  }, [institutions]);

  useEffect(() => {
    (async () => {
      if (
        departmentScheduleSearch.schoolYearId &&
        departmentScheduleSearch.institutionId &&
        !initialSearch
      ) {
        handleSearchClicked(null);
        setInitialSearch(true);
      }
    })();
  }, [
    departmentScheduleSearch.schoolYearId,
    departmentScheduleSearch.institutionId,
  ]);

  useEffect(() => {
    const request = TableUtils.GetSearchRequest<DepartmentScheduleSearch>(
      tableSearch,
      tableState
    );
    if (!request?.schoolYearId || !request?.institutionId) return;
    (async () => {
      console.log("Request:", request);
      const item = await getSchoolCandidateDepartmentProgram(request);
      console.log("Api response: ", item);

      const itemList = item.requests;

      setPrograms(item.programs || []);
      setDepartments(item.departments || []);

      //TODO GK: ovo treba urediti

      if (itemList !== null && itemList.results.length > 0) {
        const candidatesWithSelectable: (SchoolCandidateForSchedule &
          Selectable)[] = itemList.results.flatMap(
          (item) => ObjectUtils.makeSelectableArray(itemList.results)
          // ObjectUtils.makeSelectableArray(itemList.results).map(request => ({
          //     ...request,
          //     isSelected: false,
          // }))
        );

        var test = ObjectUtils.makeSelectableArray(itemList.results);

        // console.log('test');
        // console.log(test);

        // const pagedResult: PagedResult<SchoolCandidateForSchedule & Selectable> = {
        //     ...itemList,
        //     results: candidatesWithSelectable,
        // };

        const pagedResult: PagedResult<
          SchoolCandidateForSchedule & Selectable
        > = {
          ...itemList,
          results: test,
        };

        console.log("if");
        console.log(pagedResult);
        setTableData(pagedResult);
      } else {
        console.log("else");
        console.log(defaultTableData);
        setTableData(defaultTableData);
      }
      setSchoolYearEditable(request!.schoolYearId!);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  //FILTER HANDLERS
  const handleSchoolYearChange = (e: any) => {
    const schoolYear = Number.parseInt(e.target.value);
    const newDepartmentScheduleSearch: DepartmentScheduleSearch = {
      ...departmentScheduleSearch,
      schoolYearId: schoolYear,
    };
    setDepartmentScheduleSearch(newDepartmentScheduleSearch);
    setFilterChanged(true);
  };
  const setSchoolYearEditable = (yearId: number) => {
    var year = schoolYears?.find((val) => val.id === yearId);
    setIsSchoolYearEditable(!!year && year.isCurrent);
  };
  const handleProgramChange = (programId: number, requestId: number) => {
    // Ažuriramo tableData sa novim odabranim programom
    const updatedResults = tableData.results.map((item) => {
      if (item.requestId === requestId) {
        return {
          ...item,
          selectedProgram: programs.find((program) => program.id === programId),
        };
      }
      return item;
    });

    if (
      dataToUpdate.data.filter((x) => x.requestId === requestId).length === 0
    ) {
      //push
      var updatedData = updatedResults.find((x) => x.requestId === requestId);
      var syncData: SyncDataDto = {
        requestId: updatedData?.requestId!,
        departmentId: updatedData?.selectedDepartment?.id!,
        programId: updatedData?.selectedProgram?.id!,
        oib: updatedData?.oib!,
      };
      dataToUpdate.data.push(syncData);
    } else {
      var newDataToUpdate = dataToUpdate.data.slice();

      var updateIndex = dataToUpdate.data.findIndex(
        (x) => x.requestId === requestId
      );

      newDataToUpdate[updateIndex].programId = programId;

      var newUpdateData: SyncDataRequest = {
        data: newDataToUpdate,
      };

      setDataToUpdate(newUpdateData);
    }

    // Kreiramo potpuno novo stanje koje uključuje sve postojeće informacije iz tableData
    setTableData(
      (prevState) =>
        ({
          ...prevState,
          results: updatedResults,
        } as PagedResult<SchoolCandidateForSchedule & Selectable>)
    );
  };
  const handleDepartmentChange = (departmentId: number, requestId: number) => {
    //console.log('tableData');
    //console.log(tableData);
    //console.log(dataToUpdate);

    // Ažuriramo tableData sa novim odabranim departmentom
    const updatedResults = tableData.results.map((item) => {
      if (item.requestId === requestId) {
        return {
          ...item,
          selectedDepartment: departments.find(
            (department) => department.id === departmentId
          ),
        };
      }
      return item;
    });

    if (
      dataToUpdate.data.filter((x) => x.requestId === requestId).length === 0
    ) {
      //push
      var updatedData = updatedResults.find((x) => x.requestId === requestId);
      var syncData: SyncDataDto = {
        requestId: updatedData?.requestId!,
        departmentId: updatedData?.selectedDepartment?.id!,
        programId: updatedData?.selectedProgram?.id!,
        oib: updatedData?.oib!,
      };
      dataToUpdate.data.push(syncData);
    } else {
      var newDataToUpdate = dataToUpdate.data.slice();

      var updateIndex = dataToUpdate.data.findIndex(
        (x) => x.requestId === requestId
      );

      newDataToUpdate[updateIndex].departmentId = departmentId;

      var newUpdateData: SyncDataRequest = {
        data: newDataToUpdate,
      };

      setDataToUpdate(newUpdateData);
    }

    setTableData(
      (prevState) =>
        ({
          ...prevState,
          results: updatedResults,
        } as PagedResult<SchoolCandidateForSchedule & Selectable>)
    );
  };

  const handleInstitutionChange = (id: number) => {
    //console.log('institutionChange');
    //console.log(id);
    const newDepartmentScheduleSearch: DepartmentScheduleSearch = {
      ...departmentScheduleSearch,
      institutionId: !isNaN(id) ? id : 0,
    };
    setDepartmentScheduleSearch(newDepartmentScheduleSearch);
    setFilterChanged(true);
  };

  const handleClearSearchClicked = () => {
    setClearFilter(true);
    const newSearch: DepartmentScheduleSearch = {
      schoolYearId: schoolYears?.find((v) => v.isCurrent)?.id ?? 0,
      institutionId: defaultInstitutionId,
    };
    setDepartmentScheduleSearch(newSearch);
    setTableSearch(newSearch);
    setSchoolYearEditable(currentSchoolYear?.id!);
    setFilterChanged(false);
    search();
  };

  const handleSearchClicked = (e: any) => {
    setTableSearch(departmentScheduleSearch);
    setFilterChanged(false);
    search();
  };
  const search = () => {
    setFilters({ ...tableSearch });
  };

  // EDIT HANDLERS
  const handleSchoolCandidateCheckedChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const schoolCandidateForScheduleId = Number(e.target.value);
    const isChecked = e.target.checked;

    console.log(
      "clicked:",
      schoolCandidateForScheduleId,
      "tru ili fals:",
      isChecked
    );

    const updatedResults = tableData.results.map((val) => {
      if (val.requestId === schoolCandidateForScheduleId) {
        return { ...val, isSelected: isChecked };
      }
      return val;
    });

    console.log("updated results:", updatedResults);

    setTableData((prevState) => ({
      ...prevState,
      results: updatedResults,
    }));
  };

  const handleSelectAllCheckedChange = (e: any) => {
    const isChecked = !selectAll;
    setSelectAll(isChecked);

    if (
      tableData &&
      tableData.results &&
      tableData.results.filter((x) => x.synchronizationStatus === 1).length > 0
    ) {
      const newResults = tableData.results.map((val) => ({
        ...val,
        isSelected:
          val.synchronizationStatus === 1 ? isChecked : val.isSelected,
      }));

      setTableData((prevState) => ({
        ...prevState,
        results: newResults,
      }));
    }
  };

  const handleSyncDataClicked = async () => {
    const syncData = tableData.results
      .filter((x) => x.isSelected)
      .map((y) => {
        return {
          requestId: y.requestId,
          departmentId: y.selectedDepartment?.id,
          programId: y.selectedProgram?.id,
          oib: y.oib,
        };
      });

    const syncDataRequest: SyncDataRequest = {
      data: syncData,
    };

    const result = await dataSynchronization(syncDataRequest);
    console.log("pozvan sync");
    console.log(result);

    if (result.isValid) {
      setIsSuccessModalVisible(true);
      search();
    } else {
      setSyncResult(result);
      setIsErrorModalVisible(true);
    }
  };
  const handleSyncDataConfirmed = async () => {
    await handleSyncDataClicked();

    closeModal();
  };

  const handleUpdateDataConfirmed = async () => {
    const updateResult = await updateRequestsProgramAndDepartment(dataToUpdate);

    if (updateResult.isValid) {
      setUpdateSuccessModalVisible(true);
      search();
    } else {
      setUpdateDataResult(updateResult);
      setUpdateErrorModalVisible(true);
    }
  };

  if (!schoolYears || !currentSchoolYear) return <Spinner show={true} />;
  return (
    <ContentArea>
      {/* <ToolbarTop
                pageTitle={t("navigation.departmentSchedule")} 
                pageSubTitle={t("navigation.departmentScheduleSync")}
                showEdit={isSchoolYearEditable &&
                    (auth.currentRole?.role === Role.CAS
                    || auth.currentRole?.role === Role.Helpdesk
                    || auth.currentRole?.claimType === ClaimType.Institution)
                ? "show" 
                : "disable"} 
                onClickEdit={handleEditClick}
                showSave={isEditMode ? "show" : "hide"}
                onClickSave={handleSaveClick}
                showCancel={isEditMode ? "show" : "hide"}
                onClickCancel={handleCancelClick}
                errors={errors}
            /> */}
      <ToolbarTop
        pageTitle={t("navigation.departmentSchedule")}
        pageSubTitle={t("navigation.departmentScheduleSync")}
        showEdit={
          isSchoolYearEditable &&
          !isEditMode &&
          tableData &&
          tableData.results &&
          tableData.results.length > 0 &&
          (auth.currentRole?.role === Role.CAS ||
            auth.currentRole?.role === Role.Helpdesk ||
            auth.currentRole?.claimType === ClaimType.Institution)
            ? "show"
            : "disable"
        }
        onClickEdit={handleEditClick}
        errors={errors}
      />
      <SearchCard
        onClickSearch={handleSearchClicked}
        onClickClearSearch={handleClearSearchClicked}
        isFilterChanged={filterChanged}
        hideDivider={true}
      >
        <Row>
          <Col md={6}>
            <Select
              label={t("common.departmentSchedule.schoolYearId")}
              name={nameof<typeof departmentScheduleSearch>("schoolYearId")}
              value={departmentScheduleSearch.schoolYearId}
              required={true}
              onChange={handleSchoolYearChange}
              selectData={codeBookToSelectData(schoolYears)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <InstitutionSelect
              label={t("common.departmentSchedule.institutionList")}
              name={nameof<typeof departmentScheduleSearch>("institutionId")}
              //value={departmentScheduleSearch?.institutionId ?? undefined}
              value={departmentScheduleSearch.institutionId}
              countyIds={
                auth.currentRole?.claimType === ClaimType.County
                  ? auth.currentRole.locations
                  : undefined
              }
              ids={
                auth.currentRole?.claimType === ClaimType.Institution
                  ? auth.currentRole.locations
                  : undefined
              }
              onlyByEnrollmentArea={false}
              excludeHandicapped={false}
              excludePrivate={false}
              excludeSatellite={false}
              onChange={(e) =>
                handleInstitutionChange(Number.parseInt(e.target.value))
              }
              showUser={true}
              allowEmpty={false}
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Select
              infoText={t("common.departmentSchedule.syncStatusesDescription")}
              label={t("common.departmentSchedule.statusSync")}
              name={nameof<typeof departmentScheduleSearch>(
                "synchonizationStatusId"
              )}
              value={departmentScheduleSearch.synchonizationStatusId}
              onChange={handleChange(setDepartmentScheduleSearch)}
              selectData={codeBookToSelectData(statuses)}
            />
          </Col>
          <Col md={6}>
            <Select
              label={t("common.departmentSchedule.requestType")}
              name={nameof<typeof departmentScheduleSearch>("requestTypeId")}
              value={departmentScheduleSearch.requestTypeId}
              onChange={handleChange(setDepartmentScheduleSearch)}
              selectData={codeBookToSelectData(types)}
              //emptyOption={{ text: t("common.all"), value: undefined }}
            />
          </Col>
        </Row>
      </SearchCard>
      <FormCard>
        <Row className="m-1 p-2 pt-4 bg-light border">
          <Col md={2} className="pt-1">
            <Checkbox
              label={t("common.selectAll")}
              name={"selectAll"}
              checked={selectAll}
              onChange={handleSelectAllCheckedChange}
              disabled={!isSchoolYearEditable || isEditMode}
            />
          </Col>
          <Col md={10}>
            <Tooltip
              overlay={
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("common.departmentSchedule.sendDataForSync"),
                  }}
                />
              }
              placement="top"
              trigger="hover"
            >
              <Button
                onClick={openModal}
                size="sm"
                disabled={
                  (tableData &&
                    tableData.results.filter((x) => x.isSelected).length ==
                      0) ||
                  filterChanged ||
                  isEditMode
                }
                // disabled={(tableData && tableData.results.filter(x => x.isSelected).length == 0) || filterChanged}
              >
                {t("common.departmentSchedule.sendList")}
              </Button>
            </Tooltip>
          </Col>
        </Row>
      </FormCard>
      <ListTable
        tableProps={tableProps}
        pageSizeOptions={[50, 100, 200, 300, 500, 1000, 1500, 2000, 6000]}
      />
      <div className="mt-2">
        <ToolbarEdit
          showCancel={isEditMode ? "show" : "hide"}
          showSave={"hide"}
          onClickCancel={handleCancelClick}
          showCustom={isEditMode ? true : false}
        >
          <Button
            className="mr-1 float-right"
            color="primary"
            outline={false}
            size="sm"
            onClick={handleSaveClick}
          >
            {t("common.save")}
          </Button>
        </ToolbarEdit>
      </div>
      {/* {isModalVisible && (
                <Modal show={true} onHide={closeModal} centered>
                    <Modal.Body>
                        {t("common.departmentSchedule.syncConfrimModal")}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button className="btn btn-primary" onClick={closeModal}>
                            {t("common.departmentList.quit")}
                        </Button>
                        <Button className="btn btn-danger" onClick={handleSyncDataConfirmed}>
                            {t("common.departmentSchedule.send")}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}      */}

      <BSModal
        size="lg"
        backdrop="static"
        centered
        animation={false}
        show={isModalVisible}
        onHide={closeModal}
      >
        <BSModal.Header>
          {t("navigation.departmentScheduleSync")}
        </BSModal.Header>
        <BSModal.Body>
          {t("common.departmentSchedule.syncConfrimModal")}
        </BSModal.Body>
        <BSModal.Footer>
          <Button className="btn btn-primary" onClick={closeModal}>
            {t("common.departmentList.quit")}
          </Button>
          <Button className="btn btn-danger" onClick={handleSyncDataConfirmed}>
            {t("common.departmentSchedule.send")}
          </Button>
        </BSModal.Footer>
      </BSModal>

      <BSModal
        size="lg"
        backdrop="static"
        centered
        animation={false}
        show={isSuccessModalVisible}
        onHide={closeSuccessModal}
      >
        <BSModal.Header>
          {t("navigation.departmentScheduleSync")}
        </BSModal.Header>
        <BSModal.Body>
          {t("common.departmentSchedule.syncSuccessModal")}
        </BSModal.Body>
        <BSModal.Footer>
          <Button className="btn btn-primary" onClick={closeSuccessModal}>
            {t("common.departmentSchedule.close")}
          </Button>
        </BSModal.Footer>
      </BSModal>

      <BSModal
        size="lg"
        backdrop="static"
        centered
        animation={false}
        show={isErrorModalVisible}
        onHide={closeErrorModal}
      >
        <BSModal.Header>
          {t("navigation.departmentScheduleSync")}
        </BSModal.Header>
        <BSModal.Body>
          <Typography>
            {t("common.departmentSchedule.syncErrorModal")}
          </Typography>
          <Typography>
            {t("common.departmentSchedule.syncErrorModalList")}
          </Typography>
          {syncResult?.validationFailures?.map((item) => (
            <Typography>
              OIB: {item.propertyName} Greška: {item.message}
            </Typography>
          ))}
        </BSModal.Body>
        <BSModal.Footer>
          <Button className="btn btn-primary" onClick={closeErrorModal}>
            {t("common.departmentSchedule.close")}
          </Button>
        </BSModal.Footer>
      </BSModal>

      <BSModal
        size="lg"
        backdrop="static"
        centered
        animation={false}
        show={updateModalVisible}
        onHide={closeUpdateModal}
      >
        <BSModal.Header>{t("navigation.departmentSchedule")}</BSModal.Header>
        <BSModal.Body>
          {t("common.departmentSchedule.updateModal")}
        </BSModal.Body>
        <BSModal.Footer>
          <Button className="btn btn-primary" onClick={closeUpdateModal}>
            {t("common.departmentList.quit")}
          </Button>
          <Button
            className="btn btn-danger"
            onClick={handleUpdateDataConfirmed}
          >
            {t("common.departmentSchedule.save")}
          </Button>
        </BSModal.Footer>
      </BSModal>

      <BSModal
        size="lg"
        backdrop="static"
        centered
        animation={false}
        show={updateSuccessModalVisible}
        onHide={closeUpdateSuccessModal}
      >
        <BSModal.Header>{t("navigation.departmentSchedule")}</BSModal.Header>
        <BSModal.Body>
          {t("common.departmentSchedule.updateSuccessModal")}
        </BSModal.Body>
        <BSModal.Footer>
          <Button className="btn btn-primary" onClick={closeUpdateSuccessModal}>
            {t("common.departmentSchedule.close")}
          </Button>
        </BSModal.Footer>
      </BSModal>

      <BSModal
        size="lg"
        backdrop="static"
        centered
        animation={false}
        show={updateErrorModalVisible}
        onHide={closeUpdateErrorModal}
      >
        <BSModal.Header>{t("navigation.departmentSchedule")}</BSModal.Header>
        <BSModal.Body>
          <Typography>
            {t("common.departmentSchedule.updateErrorModal")}
          </Typography>
          <Typography>
            {t("common.departmentSchedule.syncErrorModalList")}
          </Typography>
          {updateDataResult?.validationFailures?.map((item) => (
            <Typography>
              OIB: {item.propertyName} Greška: {item.message}
            </Typography>
          ))}
        </BSModal.Body>
        <BSModal.Footer>
          <Button className="btn btn-primary" onClick={closeUpdateErrorModal}>
            {t("common.departmentSchedule.close")}
          </Button>
        </BSModal.Footer>
      </BSModal>
    </ContentArea>
  );
}
